<template>
  <div class="login-page" @keyup.enter="login()">

    <!-- Left: Brand Panel -->
    <div class="brand-panel">

      <!-- Road-network map background -->
      <img class="brand-bg-map" src="../../assets/roadNetworkMap.svg" alt="" aria-hidden="true" />
      <div class="brand-veil" aria-hidden="true"></div>

      <!-- Coordinate readout -->
      <div class="brand-coords">42.4240&deg; N &middot; 83.4250&deg; W</div>

      <div class="brand-content">
        <p class="brand-tagline">Map Management Platform</p>
        <ul class="brand-features">
          <li>
            <span class="brand-feature-icon"><fa :icon="['fas', 'layer-group']" /></span>
            HD maps
          </li>
          <li>
            <span class="brand-feature-icon"><fa :icon="['fas', 'globe-americas']" /></span>
            Global Geospatial Data
          </li>
          <li>
            <span class="brand-feature-icon"><fa :icon="['fas', 'eye']" /></span>
            Precision
          </li>
        </ul>
      </div>
      <div class="brand-footer">
        <a href="https://dmp-maps.com/legal/" class="brand-legal" target="_blank">
          © {{ new Date().getFullYear() }} Dynamic Map Platform North America, Inc.
        </a>
      </div>
    </div>

    <!-- New Look Notification Popup -->
    <transition name="popup-fade">
      <div v-if="showNewLookPopup" class="new-look-popup">
        <button class="new-look-close" @click="dismissNewLookPopup" title="Close">
          <fa :icon="['fas', 'times']" />
        </button>
        <div class="new-look-icon">
          <fa :icon="['fas', 'paint-brush']" />
        </div>
        <div class="new-look-text">
          <p class="new-look-title">Fresh new look incoming!</p>
          <p class="new-look-body">We’re rolling out a redesigned MMP experience—stay tuned for updates.</p>
        </div>
      </div>
    </transition>

    <!-- Right: Form Panel -->
    <div class="form-panel">
      <div class="form-stack">
        <a href="https://www.ushrauto.com/" target="_blank" class="form-logo-link">
          <img src="../../assets/dmpLogin.png" class="form-logo" alt="Dynamic Map Platform" />
        </a>
        <div class="form-card">
        <div class="form-header">
          <h2 class="form-title">Welcome back</h2>
          <p class="form-subtitle">Sign in to your account to continue</p>
        </div>

        <!-- Email -->
        <div class="field-group">
          <label class="field-label">Email</label>
          <div class="input-wrap">
            <fa :icon="['fas', 'info-circle']" class="input-icon" />
            <b-form-input
              type="email"
              name="email"
              v-model="email"
              placeholder="you@example.com"
              class="login-input"
            />
          </div>
        </div>

        <!-- Password -->
        <div class="field-group">
          <label class="field-label">Password</label>
          <div class="input-wrap">
            <fa :icon="['fas', 'lock']" class="input-icon" />
            <b-form-input
              :type="passwordVisible ? 'text' : 'password'"
              name="password"
              v-model="password"
              placeholder="••••••••"
              class="login-input"
            />
            <span
              class="eye-toggle"
              :title="passwordVisible ? 'Hide password' : 'Show password'"
              @click="togglePasswordVisibility()"
            >
              <fa :icon="['fas', passwordVisible ? 'eye-slash' : 'eye']" />
            </span>
          </div>
        </div>

        <!-- Forgot Password -->
        <div class="forgot-row">
          <b-link @click="forgotPassword()" class="forgot-link">Forgot password?</b-link>
        </div>

        <!-- Login Button -->
        <button class="login-btn" @click="login()">
          Sign In
          <!-- <fa :icon="['fas', 'arrow-right']" class="ms-2" /> -->
        </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters, mapActions } from 'vuex';
import ForgotPassword from './ForgotPassword.vue'
export default {
  name: 'Login',
  data() {
    return {
      email: null,
      password: null,
      token: null,
      feedback: null,
      passwordVisible: false,
      projectData: null,
      userData: null,
      showNewLookPopup: false
    }
  },
  computed: {
    eyeStyle() {
      return {
        right: '25px',
        top: '50%',
        transform: 'translateY(-50%)',
        cursor: 'pointer'
      }
    }
  },
  methods: {
    ...mapActions(['updateGlobalRole', 'updateGlobalAllowedUserModules']),
    login() {
      if (!this.validateLogin()) {
        this.showFeedback()
      } else {
        var self = this
        axios
          .get(process.env.API_LOCATION + '/api/auth/login', {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            params: {
              email: this.email.toLowerCase().trim(),
              password: this.password.trim()
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              this.feedback = content.errors[0].msg
              this.showFeedback()
              sessionStorage.removeItem('token')
              sessionStorage.removeItem('sessionId')
            } else {
              this.feedback = null
              sessionStorage.setItem('token', content.token)
              this.getUser()
              // Direct to home screen if successful login
            }
          })
          .catch(function (error) {
            self.feedback = error.response.data.errors[0].msg
            self.showFeedback()
          })
      } 
    },
    isValidEmail() {
      const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return pattern.test(this.email)
    },
    validateLogin() {
      if (this.email == null || this.password == null || this.email.length == 0 || this.password.length == 0) {
        this.feedback = 'Please provide both username and password.'
      } else if (!this.isValidEmail()) {
        this.feedback = 'Please provide a valid email.'
      } else if (this.password.length < 6) {
        this.feedback = 'Password length is less than 6 characters. Please provide a valid password.'
      } else if (this.password.length > 5) {
        return true
      }

      return false
    },
    getUser() {
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/auth/', {
            method: 'GET',
            headers: {
              'x-auth-token': sessionStorage.token
            }
          })
          .then(response => {
            this.userData = response.data
            if (this.userData.errors) {
             
              this.feedback = this.userData.errors[0].msg
              this.showFeedback()
              this.$router.push({name: 'Login'})
            } else {
              if (this.userData.inactive == null) {
                  sessionStorage.setItem('username', this.userData.name)
                  sessionStorage.setItem('organization', this.userData.org)
                this.feedback = null
                this.updateGlobalRole(this.userData.role_name);
                this.updateGlobalAllowedUserModules(this.userData.permitted_mmp_modules);
                this.getOrg(this.userData.org)
              } else {
                this.feedback = 'Account is inactive.'
                this.showFeedback()
                return
              }
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        this.feedback = 'No Apps For you'
        this.$router.push({name: 'Login'})
      }
    },
    getOrg(company_id) {
      if (sessionStorage.token && typeof sessionStorage.token == 'string') {
        axios
          .get(
            process.env.API_LOCATION + '/api/projects/project/' + company_id,
            {
              method: 'GET',
              headers: {
                'x-auth-token': sessionStorage.token
              }
            }
          )
          .then(response => {
             this.projectData = response.data
            if (!this.projectData.error) {
              sessionStorage.setItem('pid', this.projectData[0].id)
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        this.$router.push({name: 'Login'})
      }
      // user_roles_id 14 is for PTV suer or seperate Dashbaord
      // switch (this.userData.user_roles_id) {
      //   case 20:
      //   case 14: this.$router.push({ name: 'PTV' })
      //     break
      //   case 18: this.$router.push({ name: 'Spatial_Intelligence' })
      //     break
      //   default: this.$router.push({ name: 'MMP' })
      //     break
      // }
      if (this.userData.route) {
        if (this.userData.route == 'TRT') {
          this.$router.push({ name: `${this.userData.route}`, query: { openMap: '1' } })
        } else {
          this.$router.push({ name: `${this.userData.route}` })
        }
      } else {
        this.$router.push({ name: 'MMP' })
      }
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible
    },
    showFeedback() {
      const h = this.$createElement
      const title = h('div', { class: 'dmp-toast__title' }, [
        h('fa', { props: { icon: ['fas', 'exclamation-circle'] }, class: 'dmp-toast__icon' }),
        h('span', 'Login Failed')
      ])
      this.$bvToast.toast(this.feedback, {
        title,
        autoHideDelay: 6000,
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-top-right',
        toastClass: 'dmp-toast',
        headerClass: 'dmp-toast__header',
        bodyClass: 'dmp-toast__body'
      })
    },
    forgotPassword(){
      this.$router.push({name: 'ForgotPassword'})
    },
    dismissNewLookPopup() {
      this.showNewLookPopup = false
    }
  },
  mounted() {
    const cutoff = new Date('2026-04-15T23:59:59')
    if (new Date() <= cutoff) {
      this.showNewLookPopup = true
    }
  }
}
</script>

<style scoped>
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body, html {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  font-family: "Segoe UI", "Roboto", "Open Sans", Arial, sans-serif;
}

/* ── Page Layout ─────────────────────────────── */
.login-page {
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

/* ── Left Brand Panel ────────────────────────── */
.brand-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 3rem 2rem;
  background:
    radial-gradient(120% 90% at 15% 0%, rgba(60, 191, 111, 0.18) 0%, rgba(60, 191, 111, 0) 45%),
    radial-gradient(120% 90% at 90% 100%, rgba(41, 171, 226, 0.24) 0%, rgba(41, 171, 226, 0) 55%),
    linear-gradient(160deg, #123f68 0%, #0c2e4e 48%, #071f38 100%);
  position: relative;
  overflow: hidden;
}

/* Full-bleed road-network map */
.brand-bg-map {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0.6;
  z-index: 0;
  pointer-events: none;
}

/* Soft veil that concentrates tint behind the content for legibility */
.brand-veil {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(ellipse 62% 56% at 50% 46%,
    rgba(15, 74, 122, 0.68) 0%,
    rgba(15, 74, 122, 0.20) 55%,
    rgba(15, 74, 122, 0) 100%);
}

/* Coordinate readout chip */
.brand-coords {
  position: absolute;
  top: 1.5rem;
  left: 1.75rem;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-family: 'Courier New', monospace;
  font-size: 0.72rem;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.6);
}

.brand-coords::before {
  content: "";
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3cbf6f;
  box-shadow: 0 0 0 3px rgba(60, 191, 111, 0.25);
}

.brand-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 2.25rem;
  text-align: center;
}

.brand-tagline {
  color: rgba(255, 255, 255, 0.72);
  font-size: 1.2rem;
  font-weight: 400;
  text-transform: uppercase;
  margin: 0;
  letter-spacing: 3px;
}

.brand-features {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5rem 1.4rem;
  margin: 0;
  padding: 0;
}

.brand-features li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  position: relative;
}

/* Hairline dividers between feature labels */
.brand-features li:not(:last-child)::after {
  content: "";
  position: absolute;
  right: -0.75rem;
  height: 12px;
  width: 1px;
  background: rgba(255, 255, 255, 0.18);
}

.brand-feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #5cc0ea;
  font-size: 0.78rem;
}

.brand-image-card {
  width: clamp(220px, 28vw, 340px);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.2);
}

.brand-map-image {
  width: 100%;
  display: block;
  object-fit: cover;
  opacity: 0.92;
}

.brand-image-caption {
  padding: 0.5rem 0.85rem;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(0, 0, 0, 0.25);
  letter-spacing: 0.3px;
}

.brand-footer {
  position: relative;
  z-index: 1;
}

.brand-legal {
  color: rgba(255, 255, 255, 0.5);
    font-size: 0.95rem;
  text-decoration: none;
}

.brand-legal:hover {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: underline;
}

/* ── Right Form Panel ────────────────────────── */
.form-panel {
  width: clamp(500px, 45%, 800px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  padding: 2rem;
}

.form-stack {
  width: 100%;
  max-width: 470px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.form-logo-link {
  display: block;
  width: 100%;
  text-align: center;
  line-height: 0;
}

.form-logo {
  width: clamp(180px, 60%, 240px);
  display: inline-block;
  margin: 0 auto;
}

.form-card {
  width: 100%;
  background: #fff;
  border-radius: 14px;
  padding: 2.5rem 2.25rem;
  box-shadow: 0 4px 32px rgba(6, 60, 109, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-header {
  margin-bottom: 0.25rem;
}

.form-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #063c6d;
  margin-bottom: 0.3rem;
}

.form-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
}

/* ── Fields ──────────────────────────────────── */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.field-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #063c6d;
  margin: 0;
}

.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 13px;
  color: #4A74A8;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 2;
}

.login-input {
  padding-left: 2.4rem !important;
  padding-right: 2.6rem !important;
  height: 44px;
  border: 1.5px solid #dee2e6;
  border-radius: 8px !important;
  font-size: 0.95rem;
  color: #212529;
  background: #f8f9fa;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  width: 100%;
}

.login-input:focus {
  border-color: #4A74A8 !important;
  box-shadow: 0 0 0 3px rgba(74, 116, 168, 0.15) !important;
  background: #fff;
  outline: none;
}

.eye-toggle {
  position: absolute;
  right: 12px;
  color: #6c757d;
  cursor: pointer;
  z-index: 2;
  font-size: 0.9rem;
  transition: color 0.15s ease;
}

.eye-toggle:hover {
  color: #4A74A8;
}

/* ── Forgot Row ──────────────────────────────── */
.forgot-row {
  text-align: right;
  margin-top: -0.5rem;
}

.forgot-link {
  color: #497558 !important;
  font-size: 0.85rem;
  text-decoration: none !important;
  cursor: pointer;
}

.forgot-link:hover {
  color: #2b5a3b !important;
  text-decoration: underline !important;
}

/* ── Login Button ────────────────────────────── */
.login-btn {
  width: 100%;
  height: 46px;
  background: #4A74A8;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(74, 116, 168, 0.35);
  transition: background-color 0.18s ease, box-shadow 0.18s ease, transform 0.1s ease;
  margin-top: 0.25rem;
}

.login-btn:hover {
  background: #316fb1;
  box-shadow: 0 4px 14px rgba(74, 116, 168, 0.45);
  transform: translateY(-1px);
}

.login-btn:active {
  background: #063c6d;
  box-shadow: none;
  transform: translateY(0);
}

/* ── Responsive ──────────────────────────────── */
@media (max-width: 700px) {
  .brand-panel {
    display: none;
  }
  .form-panel {
    width: 100vw;
    padding: 1.5rem;
  }
}

/* ── New Look Popup ──────────────────────────── */
.new-look-popup {
  position: fixed;
  top: 1rem;
  left: 1.5rem;
  z-index: 9999;
  background: #fff;
  border-left: 4px solid #4A74A8;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(6, 60, 109, 0.15);
  padding: 1rem 1.25rem 1rem 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 300px;
}

.new-look-close {
  position: absolute;
  top: 0.5rem;
  right: 0.6rem;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0;
  line-height: 1;
  transition: color 0.15s ease;
}

.new-look-close:hover {
  color: #063c6d;
}

.new-look-icon {
  color: #4A74A8;
  font-size: 1.1rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.new-look-text {
  padding-right: 0.75rem;
}

.new-look-title {
  font-size: 0.90rem;
  font-weight: 700;
  color: #063c6d;
  margin: 0 0 0.25rem 0;
}

.new-look-body {
  font-size: 0.88rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.45;
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.popup-fade-enter,
.popup-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>

<!-- Toasts render in a portal outside this component, so these styles are intentionally un-scoped -->
<style>
.dmp-toast.toast {
  border: none;
  border-left: 4px solid #e5484d;
  border-radius: 12px;
  overflow: hidden;
  min-width: 340px;
  background: #ffffff;
  box-shadow: 0 14px 40px rgba(6, 60, 109, 0.22);
}

.dmp-toast .toast-header.dmp-toast__header {
  background: #ffffff;
  color: #063c6d;
  border-bottom: 1px solid #f1f3f5;
  padding: 0.7rem 0.9rem;
}

.dmp-toast__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.dmp-toast__icon {
  color: #e5484d;
  font-size: 1rem;
}

.dmp-toast .toast-body.dmp-toast__body {
  color: #495057;
  font-size: 0.9rem;
  line-height: 1.45;
  padding: 0.85rem 0.9rem;
}

.dmp-toast .close {
  color: #adb5bd;
  opacity: 1;
  text-shadow: none;
  font-weight: 400;
  transition: color 0.15s ease;
}

.dmp-toast .close:hover {
  color: #063c6d;
}
</style>
